/**
 * The Fly Agent brand (owner, 29 Sep 26, "use this as the app logo and favicon for now"):
 * the origami paper plane (violet wings, an orange inner fold) and the wordmark. The plane
 * is drawn from the owner's artwork (public/lovable-uploads/agentlogo.png, colours sampled from it) as vector shapes, so it stays crisp at 16px (favicon)
 * and 36px (sidebar) and can fly (PlaneIntro). `useFlyFavicon` swaps the tab icon to the
 * plane while a funnel page is open and puts the site's own icon back when it closes.
 */
import { useEffect, useId, useState } from 'react';
import '@/styles/agent-app-brand.css';
import { WORDMARK } from './wordmarkPaths';

/** The plane's faces in a 100 x 88 box: [id, points, gradient stops (from, to), direction]. */
const FACES = [
  { id: 'up', pts: '0,37.6 100,0 53.1,33.4', stops: ['#754cfa', '#8b60f8'], x1: 0, y1: 38, x2: 100, y2: 0 },
  { id: 'rt', pts: '100,0 49.1,86.5 53.1,33.4', stops: ['#6739f0', '#9968f9', '#d7a9fb'], x1: 78, y1: 8, x2: 50, y2: 86 },
  { id: 'lf', pts: '0,37.6 53.1,33.4 28.1,50.4', stops: ['#4a22e0', '#1e08b9'], x1: 0, y1: 38, x2: 50, y2: 36 },
  { id: 'dk', pts: '53.1,33.4 49.1,86.5 36.9,61.5', stops: ['#4d22e2', '#562ae9'], x1: 52, y1: 34, x2: 46, y2: 86 },
  { id: 'or', pts: '28.1,50.4 53.1,33.4 36.9,61.5 19.2,70.3', stops: ['#e4543d', '#ef6d44', '#feb65b'], x1: 50, y1: 36, x2: 20, y2: 70 },
] as const;

function svgMarkup(ids: (face: string) => string): string {
  const defs = FACES.map(
    (f) =>
      `<linearGradient id="${ids(f.id)}" gradientUnits="userSpaceOnUse" x1="${f.x1}" y1="${f.y1}" x2="${f.x2}" y2="${f.y2}">${f.stops
        .map((c, i, all) => `<stop offset="${all.length === 1 ? 0 : i / (all.length - 1)}" stop-color="${c}"/>`)
        .join('')}</linearGradient>`,
  ).join('');
  const polys = FACES.map((f) => `<polygon points="${f.pts}" fill="url(#${ids(f.id)})" stroke="url(#${ids(f.id)})" stroke-width="0.6" stroke-linejoin="round"/>`).join('');
  return `<defs>${defs}</defs>${polys}`;
}

/** The plane as a standalone SVG string (the favicon and the flying copy). */
export function planeSvg(size: number, prefix = 'fp'): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${Math.round((size * 88) / 100)}" viewBox="-1 -1 102 90">${svgMarkup((f) => `${prefix}-${f}`)}</svg>`;
}

export const PLANE_FAVICON = `data:image/svg+xml,${encodeURIComponent(planeSvg(64, 'fav'))}`;

/** The plane mark. `target` marks the one the intro flight lands on. */
export function PlaneMark({ size = 34, target = false, className = '' }: { size?: number; target?: boolean; className?: string }) {
  const uid = useId().replace(/[^\w-]/g, '');
  return (
    <svg
      className={`ags-plane ${className}`.trim()}
      width={size}
      height={Math.round((size * 88) / 100)}
      viewBox="-1 -1 102 90"
      aria-hidden="true"
      focusable="false"
      data-plane-target={target ? '' : undefined}
      dangerouslySetInnerHTML={{ __html: svgMarkup((f) => `${uid}-${f}`) }}
    />
  );
}

/**
 * "Fly Agent" drawn from outlines (wordmarkPaths.ts), so it never waits for the web font: Fly in ink,
 * Agent in violet, from the wordmark's own colour classes. `em` is the size the words would have
 * as text.
 */
export function WordmarkSvg({ em = 16, className = '' }: { em?: number; className?: string }) {
  return (
    <svg
      className={`ags-wm-svg ${className}`.trim()}
      width={Math.round((WORDMARK.width * em) / WORDMARK.upm)}
      height={Math.round((WORDMARK.height * em) / WORDMARK.upm)}
      viewBox={WORDMARK.viewBox}
      aria-hidden="true"
      focusable="false"
    >
      <path className="ags-wm-fly" fill="currentColor" d={WORDMARK.fly} />
      <path className="ags-wm-agent" fill="currentColor" d={WORDMARK.agent} />
    </svg>
  );
}

/**
 * The plane and "Fly Agent" for a page outside the app shell, where the shell's colour rules do not
 * reach (the set-up and sign-in page, Shardul, 27 Oct 26: "no agent logo ... or branding"). The
 * words carry their own colours, the splash's (BrandSplash), so nothing depends on the page's CSS.
 */
export function FlyAgentLockup({ size = 30, dark = false }: { size?: number; dark?: boolean }) {
  const em = Math.round(size * 0.66);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: Math.round(size * 0.3) }} role="img" aria-label="Fly Agent">
      <PlaneMark size={size} />
      <svg
        width={Math.round((WORDMARK.width * em) / WORDMARK.upm)}
        height={Math.round((WORDMARK.height * em) / WORDMARK.upm)}
        viewBox={WORDMARK.viewBox}
        aria-hidden="true"
        focusable="false"
        style={{ display: 'block', flex: 'none' }}
      >
        <path d={WORDMARK.fly} fill={dark ? '#f2f4f9' : '#121f2d'} />
        <path d={WORDMARK.agent} fill={dark ? '#a78bfa' : '#6636f0'} />
      </svg>
    </span>
  );
}

/**
 * Whether the page has scrolled away from its top: past `down` pixels it counts as scrolled, and it
 * only counts as back at the top under `up`, so a page resting near the line does not flicker. One
 * passive listener, read once a frame, and a new value only when the answer changes.
 */
export function useScrolledPast(down = 24, up = 8): boolean {
  const [past, setPast] = useState(() => typeof window !== 'undefined' && window.scrollY > down);
  useEffect(() => {
    let frame = 0;
    const read = () => {
      frame = 0;
      setPast((was) => (was ? window.scrollY > up : window.scrollY > down));
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(read);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    read();
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [down, up]);
  return past;
}

/**
 * The logo on a phone (owner, 30 Sep 26: "keep it as a floating top center logo for mobile users";
 * "when on top here it should show full name logo-fly agent and when scrolled down it should be
 * merged inside the logo in an animation type and when gone on top again it should be full logo
 * with text"): a pill hung from the middle of a phone's top bar (agent-app-brand.css) that reads
 * "Fly Agent" beside the plane at the top of the page and folds the words into the plane as the page
 * scrolls, back out at the top. Fly's plane in it is where the intro flight lands; a page's own brand
 * shows its lettered mark and its name, as the sidebar does. The top bar and the account pages' bar
 * both carry it.
 */
export function FloatLogo({ name = 'Fly', color = null }: { name?: string; color?: string | null }) {
  const fly = name.trim().toLowerCase() === 'fly';
  const folded = useScrolledPast();
  return (
    <span className="ags-float-logo" data-folded={folded ? '' : undefined} aria-hidden="true">
      {fly && !color ? (
        <PlaneMark size={22} target />
      ) : (
        <b className="ags-float-mark" style={color ? { background: color } : undefined}>
          {fly ? 'fly' : (name.trim()[0] ?? 'F').toUpperCase()}
        </b>
      )}
      <span className="ags-float-words">{fly && !color ? <WordmarkSvg em={16} /> : <span className="ags-float-name">{`${name.trim() || 'Fly'} Agent`}</span>}</span>
    </span>
  );
}

/** "Fly Agent": Fly in ink, Agent in violet. */
export function FlyWordmark({ className = '' }: { className?: string }) {
  return (
    <span className={`ags-wm ${className}`.trim()}>
      <span className="ags-wm-fly">Fly</span> <span className="ags-wm-agent">Agent</span>
    </span>
  );
}

/** The tab icon becomes the plane while a funnel page is open; the site's own icon returns after. */
export function useFlyFavicon(): void {
  useEffect(() => {
    const links = Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]'));
    const before = links.map((l) => ({ l, href: l.getAttribute('href'), type: l.getAttribute('type') }));
    let added: HTMLLinkElement | null = null;
    if (links.length) {
      for (const l of links) {
        l.setAttribute('href', PLANE_FAVICON);
        l.setAttribute('type', 'image/svg+xml');
      }
    } else {
      added = document.createElement('link');
      added.rel = 'icon';
      added.type = 'image/svg+xml';
      added.href = PLANE_FAVICON;
      document.head.appendChild(added);
    }
    return () => {
      for (const b of before) {
        if (b.href == null) b.l.removeAttribute('href');
        else b.l.setAttribute('href', b.href);
        if (b.type == null) b.l.removeAttribute('type');
        else b.l.setAttribute('type', b.type);
      }
      added?.remove();
    };
  }, []);
}
